// import { changePieceTHeme } from '../functions/changePieceTheme';

const COLUMNS: string[] = 'abcdefgh'.split('');
var pieces: any;
var defaultTheme: any;

export function piecesTheme(data: any) {
  if (!data || data === undefined) {
    pieces = 'wikipedia';
  } else {
    pieces = data;
  }

  defaultTheme = `../assets/chesspieces/${pieces}/{piece}.png`;
}

function validMove(move: string): boolean {
  // move should be a string
  if (typeof move !== 'string') return false;

  // move should be in the form of "e2-e4", "f6-d5"
  const tmp: string[] = move.split('-');
  if (tmp.length !== 2) return false;

  return validSquare(tmp[0]) === true && validSquare(tmp[1]) === true;
}

function validSquare(square: string): boolean {
  if (typeof square !== 'string') return false;
  return /^[a-h][1-8]$/.test(square);
}

function validPieceCode(code: string): boolean {
  if (typeof code !== 'string') return false;
  return /^[bw][KQRNBP]$/.test(code);
}

function validFen(fen: string): boolean {
  if (typeof fen !== 'string') return false;

  // cut off any move, castling, etc info from the end
  // we're only interested in position information
  fen = fen.replace(/ .+$/, '');

  // FEN should be 8 sections separated by slashes
  const chunks: string[] = fen.split('/');
  if (chunks.length !== 8) return false;

  // check the piece sections
  for (let i = 0; i < 8; i++) {
    if (
      chunks[i] === '' ||
      chunks[i].length > 8 ||
      /[^kqrbnpKQRNBP1-8]/.test(chunks[i])
    ) {
      return false;
    }
  }

  return true;
}

function validPositionObject(pos: Record<string, string>): boolean {
  if (typeof pos !== 'object') return false;

  for (const i in pos) {
    if (Object.prototype.hasOwnProperty.call(pos, i) !== true) continue;

    if (validSquare(i) !== true || validPieceCode(pos[i]) !== true) {
      return false;
    }
  }

  return true;
}

function fenToPieceCode(piece: string): string {
  // black piece
  if (piece.toLowerCase() === piece) {
    return 'b' + piece.toUpperCase();
  }

  // white piece
  return 'w' + piece.toUpperCase();
}

// convert bP, wK, etc code to FEN structure
function pieceCodeToFen(piece: string): string {
  const tmp = piece.split('');

  // white piece
  if (tmp[0] === 'w') {
    return tmp[1].toUpperCase();
  }

  // black piece
  return tmp[1].toLowerCase();
}

function fenToObj(fen: string): Record<string, string> | false {
  if (validFen(fen) !== true) {
    return false;
  }

  // cut off any move, castling, etc info from the end
  // we're only interested in position information
  fen = fen.replace(/ .+$/, '');

  const rows: string[] = fen.split('/');
  const position: Record<string, string> = {};

  let currentRow = 8;
  for (let i = 0; i < 8; i++) {
    const row = rows[i].split('');
    let colIndex = 0;

    // loop through each character in the FEN section
    for (let j = 0; j < row.length; j++) {
      // number / empty squares
      if (/^[1-8]$/.test(row[j])) {
        const emptySquares = parseInt(row[j], 10);
        colIndex += emptySquares;
      }
      // piece
      else {
        const square = COLUMNS[colIndex] + currentRow;
        position[square] = fenToPieceCode(row[j]);
        colIndex++;
      }
    }

    currentRow--;
  }

  return position;
}

function objToFen(obj: Record<string, string>): string | false {
  if (validPositionObject(obj) !== true) {
    return false;
  }

  let fen = '';

  let currentRow = 8;
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      const square = COLUMNS[j] + currentRow;

      // piece exists
      if (Object.prototype.hasOwnProperty.call(obj, square) === true) {
        fen += pieceCodeToFen(obj[square]);
      }

      // empty space
      else {
        fen += '1';
      }
    }

    if (i !== 7) {
      fen += '/';
    }

    currentRow--;
  }

  // squeeze the numbers together
  fen = fen.replace(/11111111/g, '8');
  fen = fen.replace(/1111111/g, '7');
  fen = fen.replace(/111111/g, '6');
  fen = fen.replace(/11111/g, '5');
  fen = fen.replace(/1111/g, '4');
  fen = fen.replace(/111/g, '3');
  fen = fen.replace(/11/g, '2');

  return fen;
}

export function ChessBoard(
  containerElOrId: string | HTMLElement,
  cfg: any = {}
): void {
  const MINIMUM_JQUERY_VERSION: string = '1.7.0';
  const START_FEN: string = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR';
  const START_POSITION: any = fenToObj(START_FEN);

  // Use unique class names to prevent clashing with anything else on the page
  // and simplify selectors
  // NOTE: these should never change
  const CSS: any = {
    alpha: 'alpha-d2270',
    black: 'black-3c85d',
    board: 'board-b72b1',
    chessboard: 'chessboard-63f37',
    clearfix: 'clearfix-7da63',
    highlight1: 'highlight1-32417',
    highlight2: 'highlight2-9c5d2',
    notation: 'notation-322f9',
    numeric: 'numeric-fc462',
    piece: 'piece-417db',
    row: 'row-5277c',
    sparePieces: 'spare-pieces-7492f',
    sparePiecesBottom: 'spare-pieces-bottom-ae20f',
    sparePiecesTop: 'spare-pieces-top-4028b',
    square: 'square-55d63',
    white: 'white-1e1d7',
  };

  let containerEl: any,
    boardEl: any,
    draggedPieceEl: any,
    sparePiecesTopEl: any,
    sparePiecesBottomEl: any;

  // Constructor return object
  const widget: any = {};

  let ANIMATION_HAPPENING: boolean = false,
    BOARD_BORDER_SIZE: number = 2,
    CURRENT_ORIENTATION: string = 'white',
    CURRENT_POSITION: any = {},
    SQUARE_SIZE: number,
    DRAGGED_PIECE: string,
    DRAGGED_PIECE_LOCATION: string,
    DRAGGED_PIECE_SOURCE: string,
    DRAGGING_A_PIECE: boolean = false,
    SPARE_PIECE_ELS_IDS: { [key: string]: string } = {},
    SQUARE_ELS_IDS: { [key: string]: string } = {},
    SQUARE_ELS_OFFSETS: any;

  function uuid(): string {
    return 'xxxx-xxxx-xxxx-xxxx-xxxx-xxxx-xxxx-xxxx'.replace(/x/g, function () {
      const r: number = (Math.random() * 16) | 0;
      return r.toString(16);
    });
  }

  function deepCopy<T>(thing: T): T {
    return JSON.parse(JSON.stringify(thing));
  }

  interface SemVer {
    major: number;
    minor: number;
    patch: number;
  }

  function parseSemVer(version: string): SemVer {
    const tmp: string[] = version.split('.');
    return {
      major: parseInt(tmp[0], 10),
      minor: parseInt(tmp[1], 10),
      patch: parseInt(tmp[2], 10),
    };
  }

  function compareSemVer(version: string, minimum: string): boolean {
    const parsedVersion: SemVer = parseSemVer(version);
    const parsedMinimum: SemVer = parseSemVer(minimum);

    const versionNum: number =
      parsedVersion.major * 10000 * 10000 +
      parsedVersion.minor * 10000 +
      parsedVersion.patch;

    const minimumNum: number =
      parsedMinimum.major * 10000 * 10000 +
      parsedMinimum.minor * 10000 +
      parsedMinimum.patch;

    return versionNum >= minimumNum;
  }
  interface ChessBoardConfig {
    showErrors?:
      | boolean
      | 'console'
      | 'alert'
      | ((code: number, msg: string, obj?: any) => void);
    // Add other properties from the original cfg object as needed
  }

  function error(code: number, msg: string, obj?: any): void {
    // Assuming cfg is defined somewhere in your code
    const cfg: ChessBoardConfig = {};

    // Do nothing if showErrors is not set
    if (!cfg.hasOwnProperty('showErrors') || cfg.showErrors === false) {
      return;
    }

    let errorText: string = 'ChessBoard Error ' + code + ': ' + msg;

    // Print to console
    if (
      cfg.showErrors === 'console' &&
      typeof console === 'object' &&
      typeof console.log === 'function'
    ) {
      if (arguments.length >= 2) {
      }
      return;
    }

    // Alert errors
    if (cfg.showErrors === 'alert') {
      if (obj) {
        errorText += '\n\n' + JSON.stringify(obj);
      }
      window.alert(errorText);
      return;
    }

    // Custom function
    if (typeof cfg.showErrors === 'function') {
      cfg.showErrors(code, msg, obj);
    }
  }

  //  error occurs
  function checkDeps(): boolean {
    // If containerId is a string, it must be the ID of a DOM node
    if (typeof containerElOrId === 'string') {
      // Cannot be empty
      if (containerElOrId === '') {
        window.alert(
          'ChessBoard Error 1001: ' +
            'The first argument to ChessBoard() cannot be an empty string.' +
            '\n\nExiting...'
        );
        return false;
      }

      // Make sure the container element exists in the DOM
      const el: HTMLElement | null = document.getElementById(containerElOrId);
      if (!el) {
        window.alert(
          'ChessBoard Error 1002: Element with id "' +
            containerElOrId +
            '" does not exist in the DOM.' +
            '\n\nExiting...'
        );
        return false;
      }

      // Set the containerEl
      containerEl = $(el);
    }

    // Else it must be something that becomes a jQuery collection
    // With size 1
    // i.e., a single DOM node or jQuery object
    else {
      containerEl = $(containerElOrId);

      if (containerEl.length !== 1) {
        window.alert(
          'ChessBoard Error 1003: The first argument to ' +
            'ChessBoard() must be an ID or a single DOM node.' +
            '\n\nExiting...'
        );
        return false;
      }
    }

    // JSON must exist
    if (
      !window.JSON ||
      typeof JSON.stringify !== 'function' ||
      typeof JSON.parse !== 'function'
    ) {
      window.alert(
        'ChessBoard Error 1004: JSON does not exist. ' +
          'Please include a JSON polyfill.\n\nExiting...'
      );
      return false;
    }

    // Check for a compatible version of jQuery
    if (
      !(
        $.fn &&
        $.fn.jquery &&
        compareSemVer($.fn.jquery, MINIMUM_JQUERY_VERSION)
      )
    ) {
      window.alert(
        'ChessBoard Error 1005: Unable to find a valid version ' +
          'of jQuery. Please include jQuery ' +
          MINIMUM_JQUERY_VERSION +
          ' or ' +
          'higher on the page.\n\nExiting...'
      );
      return false;
    }

    return true;
  }

  function validAnimationSpeed(speed: string | number): boolean {
    if (speed === 'fast' || speed === 'slow') {
      return true;
    }

    if (parseInt(speed as string, 10) + '' !== speed + '') {
      return false;
    }

    return (speed as number) >= 0;
  }

  function expandConfig(): any {
    if (typeof cfg === 'string' || validPositionObject(cfg) === true) {
      cfg = {
        position: cfg,
      };
    }

    // Default for orientation is white
    if (cfg.orientation !== 'black') {
      cfg.orientation = 'white';
    }
    CURRENT_ORIENTATION = cfg.orientation;

    // Default for showNotation is true
    if (cfg.showNotation !== false) {
      cfg.showNotation = true;
    }

    // Default for draggable is false
    if (cfg.draggable !== true) {
      cfg.draggable = false;
    }

    // Default for dropOffBoard is 'snapback'
    if (cfg.dropOffBoard !== 'trash') {
      cfg.dropOffBoard = 'snapback';
    }

    // Default for sparePieces is false
    if (cfg.sparePieces !== true) {
      cfg.sparePieces = false;
    }

    // Draggable must be true if sparePieces is enabled
    if (cfg.sparePieces === true) {
      cfg.draggable = true;
    }

    // Default piece theme is wikipedia
    // let defaultTheme = defaultPieceTheme;

    let boardPiece: any = localStorage.getItem('boardPieces');
    let boardData = JSON.parse(boardPiece);
    if (boardData) {
      defaultTheme = `../assets/chesspieces/${boardData?.pieces}/{piece}.png`;
    } else {
      defaultTheme = `../assets/chesspieces/wikipedia/{piece}.png`;
    }

    if (
      !cfg.hasOwnProperty('pieceTheme') ||
      (typeof cfg.pieceTheme !== 'string' &&
        typeof cfg.pieceTheme !== 'function')
    ) {
      // cfg.pieceTHeme = changePieceTHeme();
      cfg.pieceTheme = defaultTheme;
    }

    // Animation speeds
    if (
      !cfg.hasOwnProperty('appearSpeed') ||
      !validAnimationSpeed(cfg.appearSpeed)
    ) {
      cfg.appearSpeed = 200;
    }
    if (
      !cfg.hasOwnProperty('moveSpeed') ||
      !validAnimationSpeed(cfg.moveSpeed)
    ) {
      cfg.moveSpeed = 200;
    }
    if (
      !cfg.hasOwnProperty('snapbackSpeed') ||
      !validAnimationSpeed(cfg.snapbackSpeed)
    ) {
      cfg.snapbackSpeed = 50;
    }
    if (
      !cfg.hasOwnProperty('snapSpeed') ||
      !validAnimationSpeed(cfg.snapSpeed)
    ) {
      cfg.snapSpeed = 25;
    }
    if (
      !cfg.hasOwnProperty('trashSpeed') ||
      !validAnimationSpeed(cfg.trashSpeed)
    ) {
      cfg.trashSpeed = 100;
    }

    // Make sure position is valid
    if (cfg.hasOwnProperty('position')) {
      if (cfg.position === 'start') {
        CURRENT_POSITION = deepCopy(START_POSITION);
      } else if (validFen(cfg.position)) {
        CURRENT_POSITION = fenToObj(cfg.position);
      } else if (validPositionObject(cfg.position)) {
        CURRENT_POSITION = deepCopy(cfg.position);
      } else {
        error(7263, 'Invalid value passed to config.position.', cfg.position);
      }
    }

    return true;
  }
  function calculateSquareSize(): number {
    const containerWidth: number = parseInt(containerEl.width(), 10);

    // Defensive, prevent an infinite loop
    if (!containerWidth || containerWidth <= 0) {
      return 0;
    }

    // Pad one pixel
    let boardWidth: number = containerWidth - 1;

    while (boardWidth % 8 !== 0 && boardWidth > 0) {
      boardWidth--;
    }

    return boardWidth / 8;
  }
  function createElIds(): void {
    // Squares on the board
    for (let i = 0; i < COLUMNS.length; i++) {
      for (let j = 1; j <= 8; j++) {
        const square: string = COLUMNS[i] + j;
        SQUARE_ELS_IDS[square] = square + '-' + uuid();
      }
    }

    // Spare pieces
    const pieces: string[] = 'KQRBNP'.split('');
    for (let i = 0; i < pieces.length; i++) {
      const whitePiece: string = 'w' + pieces[i];
      const blackPiece: string = 'b' + pieces[i];
      SPARE_PIECE_ELS_IDS[whitePiece] = whitePiece + '-' + uuid();
      SPARE_PIECE_ELS_IDS[blackPiece] = blackPiece + '-' + uuid();
    }
  }
  function buildBoardContainer(): string {
    let html: string = '<div class="' + CSS.chessboard + '">';

    if (cfg.sparePieces === true) {
      html +=
        '<div class="' +
        CSS.sparePieces +
        ' ' +
        CSS.sparePiecesTop +
        '"></div>';
    }

    html += '<div class="' + CSS.board + '"></div>';

    if (cfg.sparePieces === true) {
      html +=
        '<div class="' +
        CSS.sparePieces +
        ' ' +
        CSS.sparePiecesBottom +
        '"></div>';
    }

    html += '</div>';

    return html;
  }
  function buildBoard(orientation: string): string {
    if (orientation !== 'black') {
      orientation = 'white';
    }

    let html: string = '';

    // Algebraic notation / orientation
    const alpha: string[] = deepCopy(COLUMNS);
    let row: number = 8;
    if (orientation === 'black') {
      alpha.reverse();
      row = 1;
    }

    let squareColor: string = 'white';
    for (let i = 0; i < 8; i++) {
      html += '<div class="' + CSS.row + '">';
      for (let j = 0; j < 8; j++) {
        const square: string = alpha[j] + row;

        html +=
          '<div class="' +
          CSS.square +
          ' ' +
          CSS[squareColor] +
          ' ' +
          'square-' +
          square +
          '" ' +
          'style="width: ' +
          SQUARE_SIZE +
          'px; height: ' +
          SQUARE_SIZE +
          'px" ' +
          'id="' +
          SQUARE_ELS_IDS[square] +
          '" ' +
          'data-square="' +
          square +
          '">';

        if (cfg.showNotation === true) {
          // Alpha notation
          if (
            (orientation === 'white' && row === 1) ||
            (orientation === 'black' && row === 8)
          ) {
            html +=
              '<div class="' +
              CSS.notation +
              ' ' +
              CSS.alpha +
              '">' +
              alpha[j] +
              '</div>';
          }

          // Numeric notation
          if (j === 0) {
            html +=
              '<div class="' +
              CSS.notation +
              ' ' +
              CSS.numeric +
              '">' +
              row +
              '</div>';
          }
        }

        html += '</div>'; // End .square

        squareColor = squareColor === 'white' ? 'black' : 'white';
      }
      html += '<div class="' + CSS.clearfix + '"></div></div>';

      squareColor = squareColor === 'white' ? 'black' : 'white';

      if (orientation === 'white') {
        row--;
      } else {
        row++;
      }
    }

    return html;
  }
  function buildPieceImgSrc(piece: string): string {
    if (typeof cfg.pieceTheme === 'function') {
      return cfg.pieceTheme(piece);
    }

    if (typeof cfg.pieceTheme === 'string') {
      return cfg.pieceTheme.replace(/{piece}/g, piece);
    }

    // NOTE: this should never happen
    error(8272, 'Unable to build image source for cfg.pieceTheme.');
    return '';
  }
  function buildPiece(piece: string, hidden?: boolean, id?: string): string {
    let html: string = '<img src="' + buildPieceImgSrc(piece) + '" ';

    if (id && typeof id === 'string') {
      html += 'id="' + id + '" ';
    }

    html +=
      'alt="" ' +
      'class="' +
      CSS.piece +
      '" ' +
      'data-piece="' +
      piece +
      '" ' +
      'style="width: ' +
      SQUARE_SIZE +
      'px;' +
      'height: ' +
      SQUARE_SIZE +
      'px;';

    if (hidden === true) {
      html += 'display:none;';
    }

    html += '" />';

    return html;
  }
  function buildSparePieces(color: string): string {
    let pieces: string[] = ['wK', 'wQ', 'wR', 'wB', 'wN', 'wP'];

    if (color === 'black') {
      pieces = ['bK', 'bQ', 'bR', 'bB', 'bN', 'bP'];
    }

    let html: string = '';

    for (let i = 0; i < pieces.length; i++) {
      html += buildPiece(pieces[i], false, SPARE_PIECE_ELS_IDS[pieces[i]]);
    }

    return html;
  }
  function animateSquareToSquare(
    src: string,
    dest: string,
    piece: string,
    completeFn?: () => void
  ): void {
    // Get information about the source and destination squares
    const srcSquareEl: any = $('#' + SQUARE_ELS_IDS[src]);
    const srcSquarePosition: any = srcSquareEl.offset();
    const destSquareEl: any = $('#' + SQUARE_ELS_IDS[dest]);
    const destSquarePosition: any = destSquareEl.offset();

    // Create the animated piece and absolutely position it
    // over the source square
    const animatedPieceId = uuid();
    $('body').append(buildPiece(piece, true, animatedPieceId));
    const animatedPieceEl = $('#' + animatedPieceId);
    if (srcSquarePosition) {
      animatedPieceEl.css({
        display: '',
        position: 'absolute',
        top: srcSquarePosition.top,
        left: srcSquarePosition.left,
      });
    }

    // Remove the original piece from the source square
    srcSquareEl.find('.' + CSS.piece).remove();

    // On complete
    const complete = function () {
      // Add the "real" piece to the destination square
      destSquareEl.append(buildPiece(piece));

      // Remove the animated piece
      animatedPieceEl.remove();

      // Run the complete function
      if (typeof completeFn === 'function') {
        completeFn();
      }
    };

    // Animate the piece to the destination square
    const opts = {
      duration: cfg.moveSpeed,
      complete: complete,
    };
    animatedPieceEl.animate(destSquarePosition, opts);
  }
  function animateSparePieceToSquare(
    piece: string,
    dest: string,
    completeFn?: () => void
  ): void {
    const srcOffset: any = $('#' + SPARE_PIECE_ELS_IDS[piece]).offset();
    const destSquareEl: any = $('#' + SQUARE_ELS_IDS[dest]);
    const destOffset: any = destSquareEl.offset();

    // Create the animated piece
    const pieceId = uuid();
    $('body').append(buildPiece(piece, true, pieceId));
    const animatedPieceEl = $('#' + pieceId);
    animatedPieceEl.css({
      display: '',
      position: 'absolute',
      left: srcOffset.left,
      top: srcOffset.top,
    });

    // On complete
    const complete = function () {
      // Add the "real" piece to the destination square
      destSquareEl.find('.' + CSS.piece).remove();
      destSquareEl.append(buildPiece(piece));

      // Remove the animated piece
      animatedPieceEl.remove();

      // Run complete function
      if (typeof completeFn === 'function') {
        completeFn();
      }
    };

    // Animate the piece to the destination square
    const opts = {
      duration: cfg.moveSpeed,
      complete: complete,
    };
    animatedPieceEl.animate(destOffset, opts);
  }
  function doAnimations(a: any, oldPos: any, newPos: any): void {
    if (a.length === 0) {
      return;
    }

    ANIMATION_HAPPENING = true;

    let numFinished = 0;
    function onFinish(): void {
      numFinished++;

      // Exit if not all animations are finished
      if (numFinished !== a.length) {
        return;
      }

      drawPositionInstant();
      ANIMATION_HAPPENING = false;

      // Run onMoveEnd function
      if (
        cfg.hasOwnProperty('onMoveEnd') === true &&
        typeof cfg.onMoveEnd === 'function'
      ) {
        cfg.onMoveEnd(deepCopy(oldPos), deepCopy(newPos));
      }
    }

    for (let i = 0; i < a.length; i++) {
      // Clear a piece
      if (a[i].type === 'clear') {
        $('#' + SQUARE_ELS_IDS[a[i].square] + ' .' + CSS.piece).fadeOut(
          cfg.trashSpeed,
          onFinish
        );
      }

      // Add a piece (no spare pieces)
      if (a[i].type === 'add' && cfg.sparePieces !== true) {
        $('#' + SQUARE_ELS_IDS[a[i].square])
          .append(buildPiece(a[i].piece, true))
          .find('.' + CSS.piece)
          .fadeIn(cfg.appearSpeed, onFinish);
      }

      // Add a piece from a spare piece
      if (a[i].type === 'add' && cfg.sparePieces === true) {
        animateSparePieceToSquare(a[i].piece, a[i].square, onFinish);
      }

      // Move a piece
      if (a[i].type === 'move') {
        animateSquareToSquare(
          a[i].source,
          a[i].destination,
          a[i].piece,
          onFinish
        );
      }
    }
  }
  function squareDistance(s1: any, s2: any): number {
    s1 = s1.split('');
    const s1x: number = COLUMNS.indexOf(s1[0]) + 1;
    const s1y: number = parseInt(s1[1], 10);

    s2 = s2.split('');
    const s2x: number = COLUMNS.indexOf(s2[0]) + 1;
    const s2y: number = parseInt(s2[1], 10);

    const xDelta: number = Math.abs(s1x - s2x);
    const yDelta: number = Math.abs(s1y - s2y);

    if (xDelta >= yDelta) {
      return xDelta;
    }

    return yDelta;
  }
  function createRadius(square: string): string[] {
    const squares: { square: string; distance: number }[] = [];

    // Calculate distance of all squares
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        const s: string = COLUMNS[i] + (j + 1);

        // Skip the square we're starting from
        if (square === s) continue;

        squares.push({
          square: s,
          distance: squareDistance(square, s),
        });
      }
    }

    // Sort by distance
    squares.sort((a, b) => a.distance - b.distance);

    // Just return the square code
    const squares2: string[] = [];
    for (let i = 0; i < squares.length; i++) {
      squares2.push(squares[i].square);
    }

    return squares2;
  }
  function findClosestPiece(
    position: any,
    piece: string,
    square: string
  ): string | false {
    // Create array of closest squares from square
    const closestSquares: string[] = createRadius(square);

    // Search through the position in order of distance for the piece
    for (let i = 0; i < closestSquares.length; i++) {
      const s: string = closestSquares[i];

      if (position.hasOwnProperty(s) === true && position[s] === piece) {
        return s;
      }
    }

    return false;
  }
  function calculateAnimations(pos1: any, pos2: any): any[] {
    // Make copies of both positions
    pos1 = deepCopy(pos1);
    pos2 = deepCopy(pos2);

    const animations: any[] = [];
    const squaresMovedTo: { [key: string]: boolean } = {};

    // Remove pieces that are the same in both positions
    for (const i in pos2) {
      if (!pos2.hasOwnProperty(i)) continue;

      if (pos1.hasOwnProperty(i) === true && pos1[i] === pos2[i]) {
        delete pos1[i];
        delete pos2[i];
      }
    }

    // Find all the "move" animations
    for (const i in pos2) {
      if (!pos2.hasOwnProperty(i)) continue;

      const closestPiece = findClosestPiece(pos1, pos2[i], i);
      if (closestPiece !== false) {
        animations.push({
          type: 'move',
          source: closestPiece,
          destination: i,
          piece: pos2[i],
        });

        delete pos1[closestPiece];
        delete pos2[i];
        squaresMovedTo[i] = true;
      }
    }

    // Add pieces to pos2
    for (const i in pos2) {
      if (!pos2.hasOwnProperty(i)) continue;

      animations.push({
        type: 'add',
        square: i,
        piece: pos2[i],
      });

      delete pos2[i];
    }

    // Clear pieces from pos1
    for (const i in pos1) {
      if (!pos1.hasOwnProperty(i)) continue;

      // Do not clear a piece if it is on a square that is the result
      // of a "move", i.e., a piece capture
      if (squaresMovedTo.hasOwnProperty(i) === true) continue;

      animations.push({
        type: 'clear',
        square: i,
        piece: pos1[i],
      });

      delete pos1[i];
    }

    return animations;
  }
  function drawPositionInstant(): void {
    // Clear the board
    boardEl.find('.' + CSS.piece).remove();

    // Add the pieces
    for (const i in CURRENT_POSITION) {
      if (!CURRENT_POSITION.hasOwnProperty(i)) continue;

      $('#' + SQUARE_ELS_IDS[i]).append(buildPiece(CURRENT_POSITION[i]));
    }
  }
  function drawBoard(): void {
    boardEl.html(buildBoard(CURRENT_ORIENTATION));
    drawPositionInstant();

    if (cfg.sparePieces === true) {
      if (CURRENT_ORIENTATION === 'white') {
        sparePiecesTopEl.html(buildSparePieces('black'));
        sparePiecesBottomEl.html(buildSparePieces('white'));
      } else {
        sparePiecesTopEl.html(buildSparePieces('white'));
        sparePiecesBottomEl.html(buildSparePieces('black'));
      }
    }
  }
  function calculatePositionFromMoves(position: any, moves: any): any {
    position = deepCopy(position);

    for (const i in moves) {
      if (!moves.hasOwnProperty(i)) continue;

      // Skip the move if the position doesn't have a piece on the source square
      if (!position.hasOwnProperty(i)) continue;

      const piece = position[i];
      delete position[i];
      position[moves[i]] = piece;
    }

    return position;
  }
  function setCurrentPosition(position: any): void {
    const oldPos = deepCopy(CURRENT_POSITION);
    const newPos = deepCopy(position);
    const oldFen = objToFen(oldPos);
    const newFen = objToFen(newPos);

    // Do nothing if no change in position
    if (oldFen === newFen) return;

    // Run their onChange function
    if (cfg.hasOwnProperty('onChange') && typeof cfg.onChange === 'function') {
      cfg.onChange(oldPos, newPos);
    }

    // Update state
    CURRENT_POSITION = position;
  }
  function isXYOnSquare(x: number, y: number): string {
    for (const i in SQUARE_ELS_OFFSETS) {
      if (!SQUARE_ELS_OFFSETS.hasOwnProperty(i)) continue;

      const s = SQUARE_ELS_OFFSETS[i];
      if (
        x >= s.left &&
        x < s.left + SQUARE_SIZE &&
        y >= s.top &&
        y < s.top + SQUARE_SIZE
      ) {
        return i;
      }
    }

    return 'offboard';
  }
  function captureSquareOffsets(): void {
    SQUARE_ELS_OFFSETS = {};

    for (const i in SQUARE_ELS_IDS) {
      if (!SQUARE_ELS_IDS.hasOwnProperty(i)) continue;

      SQUARE_ELS_OFFSETS[i] = $('#' + SQUARE_ELS_IDS[i]).offset();
    }
  }
  function removeSquareHighlights(): void {
    boardEl
      .find('.' + CSS.square)
      .removeClass(CSS.highlight1 + ' ' + CSS.highlight2);
  }

  function snapbackDraggedPiece(): void {
    // There is no "snapback" for spare pieces
    if (DRAGGED_PIECE_SOURCE === 'spare') {
      trashDraggedPiece();
      return;
    }

    removeSquareHighlights();

    // Animation complete
    function complete(): void {
      drawPositionInstant();
      draggedPieceEl.css('display', 'none');

      // Run their onSnapbackEnd function
      if (
        cfg.hasOwnProperty('onSnapbackEnd') &&
        typeof cfg.onSnapbackEnd === 'function'
      ) {
        cfg.onSnapbackEnd(
          DRAGGED_PIECE,
          DRAGGED_PIECE_SOURCE,
          deepCopy(CURRENT_POSITION),
          CURRENT_ORIENTATION
        );
      }
    }

    // Get source square position
    const sourceSquarePosition = $(
      '#' + SQUARE_ELS_IDS[DRAGGED_PIECE_SOURCE]
    ).offset();

    // Animate the piece to the target square
    const opts = {
      duration: cfg.snapbackSpeed,
      complete: complete,
    };
    draggedPieceEl.animate(sourceSquarePosition, opts);

    // Set state
    DRAGGING_A_PIECE = false;
  }

  function trashDraggedPiece(): void {
    removeSquareHighlights();

    // Remove the source piece
    const newPosition = deepCopy(CURRENT_POSITION);
    delete newPosition[DRAGGED_PIECE_SOURCE];
    setCurrentPosition(newPosition);

    // Redraw the position
    drawPositionInstant();

    // Hide the dragged piece
    draggedPieceEl.fadeOut(cfg.trashSpeed);

    // Set state
    DRAGGING_A_PIECE = false;
  }

  function dropDraggedPieceOnSquare(square: string): void {
    removeSquareHighlights();

    // Update position
    const newPosition = deepCopy(CURRENT_POSITION);
    delete newPosition[DRAGGED_PIECE_SOURCE];
    newPosition[square] = DRAGGED_PIECE;
    setCurrentPosition(newPosition);

    // Get target square information
    const targetSquarePosition = $('#' + SQUARE_ELS_IDS[square]).offset();

    // Animation complete
    const complete = (): void => {
      drawPositionInstant();
      draggedPieceEl.css('display', 'none');

      // Execute their onSnapEnd function
      if (
        cfg.hasOwnProperty('onSnapEnd') === true &&
        typeof cfg.onSnapEnd === 'function'
      ) {
        cfg.onSnapEnd(DRAGGED_PIECE_SOURCE, square, DRAGGED_PIECE);
      }
    };

    // Snap the piece to the target square
    const opts = {
      duration: cfg.snapSpeed,
      complete: complete,
    };
    draggedPieceEl.animate(targetSquarePosition, opts);

    // Set state
    DRAGGING_A_PIECE = false;
  }

  function beginDraggingPiece(
    source: string,
    piece: string,
    x: number,
    y: number
  ): void {
    // Run their custom onDragStart function
    // Their custom onDragStart function can cancel drag start
    if (
      typeof cfg.onDragStart === 'function' &&
      cfg.onDragStart(
        source,
        piece,
        deepCopy(CURRENT_POSITION),
        CURRENT_ORIENTATION
      ) === false
    ) {
      return;
    }

    // Set state
    DRAGGING_A_PIECE = true;
    DRAGGED_PIECE = piece;
    DRAGGED_PIECE_SOURCE = source;

    // If the piece came from spare pieces, location is offboard
    if (source === 'spare') {
      DRAGGED_PIECE_LOCATION = 'offboard';
    } else {
      DRAGGED_PIECE_LOCATION = source;
    }

    // Capture the x, y coords of all squares in memory
    captureSquareOffsets();

    // Create the dragged piece
    draggedPieceEl.attr('src', buildPieceImgSrc(piece)).css({
      display: '',
      position: 'absolute',
      left: x - SQUARE_SIZE / 2,
      top: y - SQUARE_SIZE / 2,
    });

    if (source !== 'spare') {
      // Highlight the source square and hide the piece
      $('#' + SQUARE_ELS_IDS[source])
        .addClass(CSS.highlight1)
        .find('.' + CSS.piece)
        .css('display', 'none');
    }
  }

  function updateDraggedPiece(x: number, y: number): void {
    // Put the dragged piece over the mouse cursor
    draggedPieceEl.css({
      left: x - SQUARE_SIZE / 2,
      top: y - SQUARE_SIZE / 2,
    });

    // Get location
    const location = isXYOnSquare(x, y);

    // Do nothing if the location has not changed
    if (location === DRAGGED_PIECE_LOCATION) return;

    // Remove highlight from the previous square
    if (validSquare(DRAGGED_PIECE_LOCATION) === true) {
      $('#' + SQUARE_ELS_IDS[DRAGGED_PIECE_LOCATION]).removeClass(
        CSS.highlight2
      );
    }

    // Add highlight to the new square
    if (validSquare(location) === true) {
      $('#' + SQUARE_ELS_IDS[location]).addClass(CSS.highlight2);
    }

    // Run onDragMove
    if (typeof cfg.onDragMove === 'function') {
      cfg.onDragMove(
        location,
        DRAGGED_PIECE_LOCATION,
        DRAGGED_PIECE_SOURCE,
        DRAGGED_PIECE,
        deepCopy(CURRENT_POSITION),
        CURRENT_ORIENTATION
      );
    }

    // Update state
    DRAGGED_PIECE_LOCATION = location;
  }

  function stopDraggedPiece(location: string): void {
    // Determine what the action should be
    let action: string = 'drop';
    if (location === 'offboard' && cfg.dropOffBoard === 'snapback') {
      action = 'snapback';
    }
    if (location === 'offboard' && cfg.dropOffBoard === 'trash') {
      action = 'trash';
    }

    // Run their onDrop function, which can potentially change the drop action
    if (
      cfg.hasOwnProperty('onDrop') === true &&
      typeof cfg.onDrop === 'function'
    ) {
      const newPosition = deepCopy(CURRENT_POSITION);

      // Source piece is a spare piece and position is off the board
      // if (DRAGGED_PIECE_SOURCE === 'spare' && location === 'offboard') {...}
      // Position has not changed; do nothing

      // Source piece is a spare piece and position is on the board
      if (DRAGGED_PIECE_SOURCE === 'spare' && validSquare(location) === true) {
        // Add the piece to the board
        newPosition[location] = DRAGGED_PIECE;
      }

      // Source piece was on the board and position is off the board
      if (
        validSquare(DRAGGED_PIECE_SOURCE) === true &&
        location === 'offboard'
      ) {
        // Remove the piece from the board
        delete newPosition[DRAGGED_PIECE_SOURCE];
      }

      // Source piece was on the board and position is on the board
      if (
        validSquare(DRAGGED_PIECE_SOURCE) === true &&
        validSquare(location) === true
      ) {
        // Move the piece
        delete newPosition[DRAGGED_PIECE_SOURCE];
        newPosition[location] = DRAGGED_PIECE;
      }

      const oldPosition = deepCopy(CURRENT_POSITION);

      const result = cfg.onDrop(
        DRAGGED_PIECE_SOURCE,
        location,
        DRAGGED_PIECE,
        newPosition,
        oldPosition,
        CURRENT_ORIENTATION
      );

      if (result === 'snapback' || result === 'trash') {
        action = result;
      }
    }

    // Do it!
    if (action === 'snapback') {
      snapbackDraggedPiece();
    } else if (action === 'trash') {
      trashDraggedPiece();
    } else if (action === 'drop') {
      dropDraggedPieceOnSquare(location);
    }
  }

  widget.clear = function (useAnimation: any) {
    widget.position({}, useAnimation);
  };

  widget.destroy = function () {
    // remove markup
    containerEl.html('');
    draggedPieceEl.remove();

    // remove event handlers
    containerEl.unbind();
  };
  widget.fen = function () {
    return widget.position('fen');
  };
  widget.flip = function () {
    return widget.orientation('flip');
  };
  widget.move = function () {
    // no need to throw an error here; just do nothing
    if (arguments.length === 0) return;

    var useAnimation = true;

    // collect the moves into an object
    var moves: any = {};
    for (var i = 0; i < arguments.length; i++) {
      // any "false" to this function means no animations
      if (arguments[i] === false) {
        useAnimation = false;
        continue;
      }

      // skip invalid arguments
      if (validMove(arguments[i]) !== true) {
        error(2826, 'Invalid move passed to the move method.', arguments[i]);
        continue;
      }

      var tmp = arguments[i].split('-');
      moves[tmp[0]] = tmp[1];
    }

    // calculate position from moves
    var newPos = calculatePositionFromMoves(CURRENT_POSITION, moves);

    // update the board
    widget.position(newPos, useAnimation);

    // return the new position object
    return newPos;
  };
  widget.orientation = function (arg: any): string {
    // no arguments, return the current orientation
    if (arguments.length === 0) {
      return CURRENT_ORIENTATION;
    }

    // set to white or black
    if (arg === 'white' || arg === 'black') {
      CURRENT_ORIENTATION = arg;
      drawBoard();
      return CURRENT_ORIENTATION;
    }

    // flip orientation
    if (arg === 'flip') {
      CURRENT_ORIENTATION = CURRENT_ORIENTATION === 'white' ? 'black' : 'white';
      drawBoard();
      return CURRENT_ORIENTATION;
    }

    error(5482, 'Invalid value passed to the orientation method.', arg);

    return CURRENT_ORIENTATION;
  };

  widget.position = function (position: any, useAnimation: any) {
    // no arguments, return the current position
    if (arguments.length === 0) {
      return deepCopy(CURRENT_POSITION);
    }

    // get position as FEN
    if (typeof position === 'string' && position.toLowerCase() === 'fen') {
      return objToFen(CURRENT_POSITION);
    }

    // default for useAnimations is true
    if (useAnimation !== false) {
      useAnimation = true;
    }

    // start position
    if (typeof position === 'string' && position.toLowerCase() === 'start') {
      position = deepCopy(START_POSITION);
    }

    // convert FEN to position object
    if (validFen(position) === true) {
      position = fenToObj(position);
    }

    // validate position object
    if (validPositionObject(position) !== true) {
      error(6482, 'Invalid value passed to the position method.', position);
      return;
    }

    if (useAnimation === true) {
      // start the animations
      doAnimations(
        calculateAnimations(CURRENT_POSITION, position),
        CURRENT_POSITION,
        position
      );

      // set the new position
      setCurrentPosition(position);
    }
    // instant update
    else {
      setCurrentPosition(position);
      drawPositionInstant();
    }
  };

  widget.resize = function () {
    // calulate the new square size
    SQUARE_SIZE = calculateSquareSize();

    // set board width
    boardEl.css('width', SQUARE_SIZE * 8 + 'px');

    // set drag piece size
    draggedPieceEl.css({
      height: SQUARE_SIZE,
      width: SQUARE_SIZE,
    });

    // spare pieces
    if (cfg.sparePieces === true) {
      containerEl
        .find('.' + CSS.sparePieces)
        .css('paddingLeft', SQUARE_SIZE + BOARD_BORDER_SIZE + 'px');
    }

    // redraw the board
    drawBoard();
  };
  widget.start = function (useAnimation: any) {
    widget.position('start', useAnimation);
  };
  function isTouchDevice() {
    return 'ontouchstart' in document.documentElement;
  }
  function isMSIE() {
    return (
      navigator &&
      navigator.userAgent &&
      navigator.userAgent.search(/MSIE/) !== -1
    );
  }
  function stopDefault(e: any) {
    e.preventDefault();
  }
  function mousedownSquare(this: any, e: any): void {
    // Do nothing if we're not draggable
    if (cfg.draggable !== true) return;

    const square: any = $(this).attr('data-square');

    // No piece on this square
    if (
      validSquare(square) !== true ||
      CURRENT_POSITION.hasOwnProperty(square) !== true
    ) {
      return;
    }

    beginDraggingPiece(square, CURRENT_POSITION[square], e.pageX, e.pageY);
  }

  function touchstartSquare(this: any, e: any) {
    // do nothing if we're not draggable
    if (cfg.draggable !== true) return;

    var square: any = $(this).attr('data-square');

    // no piece on this square
    if (
      validSquare(square) !== true ||
      CURRENT_POSITION.hasOwnProperty(square) !== true
    ) {
      return;
    }

    e = e.originalEvent;
    beginDraggingPiece(
      square,
      CURRENT_POSITION[square],
      e.changedTouches[0].pageX,
      e.changedTouches[0].pageY
    );
  }

  function mousedownSparePiece(this: any, e: any) {
    // do nothing if sparePieces is not enabled
    if (cfg.sparePieces !== true) return;

    var piece: any = $(this).attr('data-piece');

    beginDraggingPiece('spare', piece, e.pageX, e.pageY);
  }

  function touchstartSparePiece(this: any, e: any) {
    // do nothing if sparePieces is not enabled
    if (cfg.sparePieces !== true) return;

    var piece: any = $(this).attr('data-piece');

    e = e.originalEvent;
    beginDraggingPiece(
      'spare',
      piece,
      e.changedTouches[0].pageX,
      e.changedTouches[0].pageY
    );
  }

  function mousemoveWindow(e: any) {
    // do nothing if we are not dragging a piece
    if (DRAGGING_A_PIECE !== true) return;

    updateDraggedPiece(e.pageX, e.pageY);
  }

  function touchmoveWindow(e: any) {
    // do nothing if we are not dragging a piece
    if (DRAGGING_A_PIECE !== true) return;

    // prevent screen from scrolling
    e.preventDefault();

    updateDraggedPiece(
      e.originalEvent.changedTouches[0].pageX,
      e.originalEvent.changedTouches[0].pageY
    );
  }

  function mouseupWindow(e: any) {
    // do nothing if we are not dragging a piece
    if (DRAGGING_A_PIECE !== true) return;

    // get the location
    var location = isXYOnSquare(e.pageX, e.pageY);

    stopDraggedPiece(location);
  }

  function touchendWindow(e: any) {
    // do nothing if we are not dragging a piece
    if (DRAGGING_A_PIECE !== true) return;

    // get the location
    var location = isXYOnSquare(
      e.originalEvent.changedTouches[0].pageX,
      e.originalEvent.changedTouches[0].pageY
    );

    stopDraggedPiece(location);
  }
  function mouseenterSquare(e: any) {
    // do not fire this event if we are dragging a piece
    // NOTE: this should never happen, but it's a safeguard
    if (DRAGGING_A_PIECE !== false) return;

    if (
      cfg.hasOwnProperty('onMouseoverSquare') !== true ||
      typeof cfg.onMouseoverSquare !== 'function'
    )
      return;

    // get the square
    var square: any = $(e.currentTarget).attr('data-square');

    // NOTE: this should never happen; defensive
    if (validSquare(square) !== true) return;

    // get the piece on this square
    var piece = false;
    if (CURRENT_POSITION.hasOwnProperty(square) === true) {
      piece = CURRENT_POSITION[square];
    }

    // execute their function
    cfg.onMouseoverSquare(
      square,
      piece,
      deepCopy(CURRENT_POSITION),
      CURRENT_ORIENTATION
    );
  }

  function mouseleaveSquare(e: any) {
    // do not fire this event if we are dragging a piece
    // NOTE: this should never happen, but it's a safeguard
    if (DRAGGING_A_PIECE !== false) return;

    if (
      cfg.hasOwnProperty('onMouseoutSquare') !== true ||
      typeof cfg.onMouseoutSquare !== 'function'
    )
      return;

    // get the square
    var square: any = $(e.currentTarget).attr('data-square');

    // NOTE: this should never happen; defensive
    if (validSquare(square) !== true) return;

    // get the piece on this square
    var piece = false;
    if (CURRENT_POSITION.hasOwnProperty(square) === true) {
      piece = CURRENT_POSITION[square];
    }

    // execute their function
    cfg.onMouseoutSquare(
      square,
      piece,
      deepCopy(CURRENT_POSITION),
      CURRENT_ORIENTATION
    );
  }

  function addEvents(): void {
    // Prevent browser "image drag"
    $('body').on('mousedown mousemove', '.' + CSS.piece, stopDefault);

    // Mouse drag pieces
    boardEl.on('mousedown', '.' + CSS.square, mousedownSquare);
    containerEl.on(
      'mousedown',
      '.' + CSS.sparePieces + ' .' + CSS.piece,
      mousedownSparePiece
    );

    // Mouse enter / leave square
    boardEl
      .on('mouseenter', '.' + CSS.square, mouseenterSquare)
      .on('mouseleave', '.' + CSS.square, mouseleaveSquare);

    // IE doesn't like the events on the window object, but other browsers
    // perform better that way
    if (isMSIE() === true) {
      // IE-specific prevent browser "image drag"
      document.ondragstart = function () {
        return false;
      };

      $('body').on('mousemove', mousemoveWindow).on('mouseup', mouseupWindow);
    } else {
      $(window).on('mousemove', mousemoveWindow).on('mouseup', mouseupWindow);
    }

    // Touch drag pieces
    if (isTouchDevice() === true) {
      boardEl.on('touchstart', '.' + CSS.square, touchstartSquare);
      containerEl.on(
        'touchstart',
        '.' + CSS.sparePieces + ' .' + CSS.piece,
        touchstartSparePiece
      );
      $(window).on('touchmove', touchmoveWindow).on('touchend', touchendWindow);
    }
  }

  function initDom(): void {
    // Create unique IDs for all the elements we will create
    createElIds();

    // Build board and save it in memory
    containerEl.html(buildBoardContainer());
    boardEl = containerEl.find('.' + CSS.board);

    if (cfg.sparePieces === true) {
      sparePiecesTopEl = containerEl.find('.' + CSS.sparePiecesTop);
      sparePiecesBottomEl = containerEl.find('.' + CSS.sparePiecesBottom);
    }

    // Create the drag piece
    const draggedPieceId = uuid();
    $('body').append(buildPiece('wP', true, draggedPieceId));
    draggedPieceEl = $('#' + draggedPieceId);

    // Get the border size
    BOARD_BORDER_SIZE = parseInt(boardEl.css('borderLeftWidth'), 10);

    // Set the size and draw the board
    widget.resize();
  }
  function init() {
    if (checkDeps() !== true || expandConfig() !== true) return;

    initDom();
    addEvents();
  }

  // go time
  init();

  return widget;
}

ChessBoard.fenToObj = fenToObj;
ChessBoard.objToFen = objToFen;
